<style scoped>

  .file-upload-segment {
    margin-top: 10px;
    min-height: 350px;
  }

  .tip-label {
    float: right; 
    margin-top: 2px;
  }

</style>

<template>
  
  <div class="ui segment file-upload-segment" :class="{ loading, secondary: dragging, 'basic no-padding no-margin': basic }" draggable="true" 
    @dragover="onDragOver($event)" @dragleave="onDragLeave($event)" @drop="onDrop($event)">          

    <h4 v-if="header" class="ui dividing header">{{ header }}</h4>

    <input v-show="false" type="file" :id="'file-upload-input-' + _uid" ref="file" @change="onFileChange()">          

    <button v-if="!readOnly" class="ui mini basic button" @click="onSendFile()">
      <i class="upload blue icon"></i> {{ sendButtonLabel }}
    </button>

    <div class="desktop-only" :class="dragTip.labelClass">            
      <i :class="dragTip.icon"></i> {{ dragTip.text }}
    </div>                     

    <p v-if="!loading && files.length == 0" class="grey text m-t-20">
      <i class="info circle icon"></i> {{ noUploadedFilesLabel }}
    </p>

    <div v-if="fileType == 'image' && files.length > 0" class="ui basic segment">            
      <div :class="cardsClass">
        <div v-for="(file, index) in files" class="card" :class="{ 'animated zoomOut': file.zoomOut }">                
          <div class="content">
            <img class="ui fluid middle aligned image" :src="file.url">
          </div>
          <div v-if="!readOnly" class="ui bottom attached button" :class="{ loading: file.removing, disabled: removing }" 
            @click="onRemoveFile(index)">
            <i class="times icon"></i> Remover
          </div>
        </div>
      </div>      
    </div>

    <table v-if="fileType != 'image' && files.length > 0" class="ui celled selectable tablet stackable table m-t-20">
      <thead v-if="$isDesktop">
        <tr>
          <th>Arquivo</th>            
          <th v-if="!readOnly"></th>
        </tr>  
      </thead>
      <tbody>
        <tr v-for="(file, index) in files" :class="{ 'animated zoomOut': file.zoomOut }">
          <td>
            <a :href="file.url">{{ file.name }}</a>                  
          </td>
          <td v-if="!readOnly" style="width: 70px !important; text-align: center;">
            <button class="ui small icon button" :class="{ loading: file.removing, disabled: removing }" 
              @click="onRemoveFile(index)">
              <i class="times icon"></i>
            </button>
          </td>
        </tr>
      </tbody>
    </table>

  </div>

</template>

<script>

  import axios from "axios"
  import clearSpecialChars from "@/utils/clear-special-chars"

  export default {
    
    name: "FileUpload",

    data() {
      return {
        loading: true,
        dragging: false,
        uploading: false,
        removing: false,
        file: null,
        files: [],
      }
    },

    props: {
      uploadPath: { type: String, required: true },
      loader: { type: Function, required: true },
      header: { type: String },
      fileType: { type: String, default: "any" },
      singleFile: { type: Boolean, default: false },
      readOnly: { type: Boolean, default: false },
      basic: { type: Boolean, default: false },
      sendButtonLabel: { type: String, default: "Enviar arquivo" },
      dragTipLabel: { type: String, default: "Você também pode arrastar os arquivos aqui" },
      sendingLabel: { type: String, default: "Enviando arquivos..." },
      dropToUploadLabel: { type: String, default: "Solte para enviar" },
      noUploadedFilesLabel: { type: String, default: "Nenhum arquivo enviado" },
      imageCardsClass: { type: String, default: "ui four stackable cards" },
    },

    computed: {

      dragTip() {

        if (this.readOnly)
          return {}

        let labelClass = "ui small label tip-label"
        let icon = "lightbulb outline purple icon"
        let text = this.dragTipLabel

       if (this.uploading) {

          labelClass += " blue"
          icon = "spinner circle loading icon"
          text = this.sendingLabel      
        
        } else if (this.dragging) {
          
          labelClass += " green"
          icon = "info circle icon"
          text = this.dropToUploadLabel
        }  

        return { labelClass, icon, text }
      },

      cardsClass() {
        return this.singleFile ? "" : this.imageCardsClass
      },
    },

    methods: {

      reset() {        
        this.loading = false
        this.dragging = false
        this.uploading = false
        this.removing = false
        this.file = null
        this.files = []
      },

      async loadFiles() {

        this.loading = true

        try {

          const data = await this.loader()

          if (data) {
            this.files = this.singleFile ? [data] : data
          } else {
            this.files = []
          }
          
        } catch (error) {
          
          this.$errorHandler(error)
        
        } finally {

          this.loading = false
        }
      },

      onDragOver($event) {
        
        $event.preventDefault()
        $event.stopPropagation()

        if (!this.readOnly)
          this.dragging = true
      },

      onDragLeave($event) {

        $event.preventDefault()
        $event.stopPropagation()

        this.dragging = false  
      },

      async onDrop($event) {
        
        $event.preventDefault()
        $event.stopPropagation()

        if (this.readOnly)
          return

        const df = $event.dataTransfer

        if (!df) {
          this.dragging = false
          return
        }

        if (this.singleFile && this.files.length > 0)
          await this.removeFile(0)

        let files = []
        let uploadedFiles = 0

        if (df.items) {          
          for (let index = 0; index < df.items.length; index++) {
            if (df.items[index].kind === "file" && this.isFile(df.items[index]))
              files.push(df.items[index].getAsFile())
          }
        } else if (df.files) {
          for (let index = 0; index < df.files.length; index++) {
            files.push(df.files[index])
          }
        }          

        for (let index = 0; index < files.length; index++) {

          if (this.singleFile && uploadedFiles == 1)
            continue

          this.file = files[index]
          
          await this.onUpload()
          
          uploadedFiles++
        }                  

        this.dragging = false        

        if (uploadedFiles == 0)
          this.$msg.warn(this.noUploadedFilesLabel)
      },

      isFile(item) {

        let isFile = true

        if (typeof item.webkitGetAsEntry == "function")
          isFile = item.webkitGetAsEntry().isFile

        return isFile
      },

      onSendFile() {
        $("#file-upload-input-" + this._uid).click()
      },

      onFileChange() {
        
        this.file = this.$refs.file.files[0]

        if (this.file)
          this.onUpload()
      }, 

      async onUpload() {

        if (this.uploading)
          return          
        
        this.uploading = true

        try {

          const url = process.env.VUE_APP_API_URL + this.uploadPath

          const headers = {
            "Content-Type": "multipart/form-data",
            "auth-token": await this.$store.state.auth.fbUser.getIdToken(true),
          }

          let formData = new FormData()          
          let fileName = this.clearFileName(this.file.name)
          
          formData.append("file", this.file, fileName)

          await axios.post(url, formData, { headers })                            

          this.$emit("onUpload", { file: this.file, fileName })

          this.uploading = false

          this.loadFiles()
          
        } catch (error) {          

          this.$errorHandler(error)
        
        } finally {

          this.uploading = false
        }
      },

      clearFileName(name) {

        let cleanName = clearSpecialChars(name)

        return cleanName.replace(/[^a-zA-Z0-9-_\.]/g, '')
      },

      onRemoveFile(index) {

        if (this.removing)
          return

        this.removing = true

        let files = this.files.slice()

        files[index].removing = true

        this.files = files.slice()

        setTimeout(() => {
          files[index].zoomOut = true
          this.removeFile(index)
        }, 800)        
      },

      async removeFile(index) {

        if (!index)
          index = 0

        this.removing = true

        try {    
          
          const file = this.$cloneObject(this.files[index])

          this.files.splice(index, 1)

          this.$emit("onRemove", file)
          
        } catch (error) {

          this.$errorHandler(error) 
        
        } finally {

          this.removing = false
        }        
      },
    },
  }

</script>